import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import usePusher from '../../../../helpers/hooks/usePusher';
import type { RootState } from '../../../../state/store';
import type { ToastContextType } from '../../../../types/toast';
import { ToastContext } from '../../../../helpers/contexts/toastContext';
import { exportRefreshEventEmitter } from '../../services/exportRefreshEventEmitter';

const ExportProvider = ({ children }: { children: React.ReactNode }) => {
  const { pusherInstance } = usePusher();
  const { showToast } = useContext(ToastContext) as ToastContextType;
  const location = useLocation();
  const company_id = useSelector((state: RootState) => state.company.company_details.company_id);
  const user_id = useSelector((state: RootState) => (state.company.company_details as any).user_id);

  useEffect(() => {
    // Only set up Pusher subscription if we have necessary data
    if (!pusherInstance || !company_id || !user_id) {
      return;
    }

    // Use the correct channel name that matches the backend
    const channelName = `private-exports-company-${company_id}-user-${user_id}`;

    try {
      const channel = pusherInstance.subscribe(channelName);

      // Only listen for export_completed events (status_update events don't exist in backend)
      channel.bind('export_completed', (data: any) => {
        // Show toast notification when export is ready
        showToast({
          message: `Export complete! Your file is ready for download.`,
          type: 'success'
        });

        // Notify any open export modals about completion
        exportRefreshEventEmitter.notifyExportCompleted(data);

        // Refresh the export history if user is on the export history page
        if (location.pathname === '/saved-reports') {
          exportRefreshEventEmitter.refreshExportHistory();
        }
      });

      // Handle subscription errors
      channel.bind('pusher:subscription_error', (error: any) => {
        console.error('SavedReports Pusher subscription error:', error);
      });
    } catch (error) {
      console.error('SavedReports: Failed to subscribe to Pusher channel:', error);
    }

    // Cleanup on unmount
    return () => {
      if (pusherInstance) {
        try {
          pusherInstance.unsubscribe(channelName);
          console.log(`SavedReports: Unsubscribed from channel: ${channelName}`);
        } catch (error) {
          console.error('SavedReports: Error unsubscribing:', error);
        }
      }
    };
  }, [pusherInstance, company_id, user_id, location.pathname]);

  return <>{children}</>;
};

export default ExportProvider;
